import { parseEther } from 'viem';
import type { Maybe } from '@soundxyz/utils';

/*
 *   Unexported helpers that mirror contract logic for buy/sell price computation
 */

// Translated logic in BondingCurveLib.sol::sigmoid2Sum to efficiently compute the
// closed form sum of the sigmod from currentSupply to currentSupply + quantity
function sigmoid2Sum({
  inflectionPoint,
  inflectionPrice,
  currentSupply,
  quantity,
}: {
  inflectionPoint: number;
  inflectionPrice: string;
  currentSupply: number;
  quantity: number;
}): bigint {
  let sum = 0n;
  const g = inflectionPoint;
  const h = BigInt(inflectionPrice);

  let s = currentSupply + 1;
  const end = s + quantity;
  const quadraticEnd = Math.min(g, end);

  if (s < quadraticEnd) {
    const k = currentSupply;
    const n = quadraticEnd - 1;
    const a = h / BigInt(g * g);
    sum = a * BigInt((n * (n + 1) * (2 * n + 1) - k * (k + 1) * (2 * k + 1)) / 6);
    s = quadraticEnd;
  }

  if (s < end) {
    const c = (3 * g) >> 2;
    const h2 = h << 1n;
    do {
      const r = Math.sqrt((s - c) * g);
      sum = sum + (h2 * BigInt(Math.round(r))) / BigInt(g);
    } while (++s !== end);
  }

  return sum;
}

// Translated logic in BondingCurveLib.sol::linearSum to efficiently compute the
// closed form sum of the linear from currentSupply to currentSupply + quantity
function linearSum({
  linearPriceSlope,
  currentSupply,
  quantity,
}: {
  linearPriceSlope: bigint;
  currentSupply: number;
  quantity: number;
}): bigint {
  const m = linearPriceSlope;
  const k = currentSupply;
  const n = k + quantity;
  return m * BigInt((n * (n + 1) - k * (k + 1)) >> 1);
}

type TotalPriceArgs = {
  basePrice: string;
  linearPriceSlope: bigint;
  inflectionPoint: number;
  inflectionPrice: string;
  currentSupply: number;
  quantity: number;
};

// Mirrors Sam.sol::_subTotal which adds the base price, linear sum, and sigmoid sum
function totalPrice({
  basePrice,
  linearPriceSlope,
  inflectionPoint,
  inflectionPrice,
  currentSupply,
  quantity,
}: TotalPriceArgs) {
  return (
    BigInt(basePrice) * BigInt(quantity) +
    (linearSum({ linearPriceSlope, currentSupply, quantity }) +
      sigmoid2Sum({ inflectionPoint, inflectionPrice, currentSupply, quantity }))
  );
}

type SellPriceArgs = TotalPriceArgs;

// mirrors logic to calculate sell price in contract
function totalSellPrice({
  basePrice,
  linearPriceSlope,
  inflectionPoint,
  inflectionPrice,
  currentSupply,
  quantity,
}: SellPriceArgs) {
  const supplyMinusQuantity = currentSupply - quantity;

  return totalPrice({
    basePrice,
    linearPriceSlope,
    inflectionPoint,
    inflectionPrice,
    currentSupply: supplyMinusQuantity,
    quantity,
  });
}

type BuyPriceArgs = TotalPriceArgs & BuyFeeArgs;

// mirrors logic to calculate buy price in contract
function buyValueBeforeBuffer({
  basePrice,
  linearPriceSlope,
  inflectionPoint,
  inflectionPrice,
  currentSupply,
  quantity,
  artistFeeBPS,
  goldenEggFeeBPS,
  affiliateFeeBPS,
  platformFeeBPS,
}: BuyPriceArgs) {
  const buyFeeBPS = artistFeeBPS + goldenEggFeeBPS + affiliateFeeBPS + platformFeeBPS;

  const p = totalPrice({
    basePrice,
    linearPriceSlope,
    inflectionPoint,
    inflectionPrice,
    currentSupply,
    quantity,
  });

  return (p * (10_000n + BigInt(buyFeeBPS))) / 10_000n;
}

/*
 *   Exported helpers for buy/sell/milestone which apply buffers on computed contract values
 */

// price should never deviate more than 0.25 eth from the contract price
export const MAX_BUFFER_SLIPPAGE = parseEther('0.25');
const HUNDRED_PERCENT_BPS = 10_000n;
// 10% buy buffer
const DEFAULT_BUY_BUFFER_BPS = 1000n;
// 10% sell buffer
const DEFAULT_SELL_BUFFER_BPS = 1000n;

type SellPriceWithBufferArgs = SellPriceArgs & { bufferOverrideBps: Maybe<number> };

export function totalSellPriceWithBuffer({
  bufferOverrideBps,
  ...sellPriceArgs
}: SellPriceWithBufferArgs) {
  const contractTotalPrice = totalSellPrice(sellPriceArgs);

  // default of 10% less than sell price as buffer
  const sellBufferBps =
    bufferOverrideBps != null ? BigInt(bufferOverrideBps) : DEFAULT_SELL_BUFFER_BPS;
  const percentBuffer =
    (contractTotalPrice * (HUNDRED_PERCENT_BPS - sellBufferBps)) / HUNDRED_PERCENT_BPS;

  // safeguard to ensure no buffer percent causes price to deviate beyond the max slippage
  const fixedBuffer = contractTotalPrice - MAX_BUFFER_SLIPPAGE;
  // use the greater of the two buffered prices
  const bufferedPrice = fixedBuffer > percentBuffer ? fixedBuffer : percentBuffer;

  const totalBasePrice = BigInt(sellPriceArgs.basePrice) * BigInt(sellPriceArgs.quantity);

  // don't show any totals less than the totalBasePrice
  return bufferedPrice > totalBasePrice ? bufferedPrice : totalBasePrice;
}

type BuyFeeArgs = {
  artistFeeBPS: number;
  goldenEggFeeBPS: number;
  affiliateFeeBPS: number;
  platformFeeBPS: number;
};

type BuyPriceWithBufferArgs = BuyPriceArgs & { bufferOverrideBps: Maybe<number> };

export function totalBuyPriceWithBuffer({
  bufferOverrideBps,
  ...buyPriceArgs
}: BuyPriceWithBufferArgs) {
  const contractBuyPrice = buyValueBeforeBuffer(buyPriceArgs);

  // default of 10% more than buy price as buffer
  const buyBufferBps =
    bufferOverrideBps != null ? BigInt(bufferOverrideBps) : DEFAULT_BUY_BUFFER_BPS;
  const percentBuffer =
    (contractBuyPrice * (HUNDRED_PERCENT_BPS + buyBufferBps)) / HUNDRED_PERCENT_BPS;

  // safeguard to ensure no buffer percent causes price to deviate beyond the max slippage
  const fixedBuffer = contractBuyPrice + MAX_BUFFER_SLIPPAGE;
  // return the smaller of the two
  return fixedBuffer > percentBuffer ? percentBuffer : fixedBuffer;
}

// Computes the next decimal increment from the current price
// E.g. if currentPrice is 0.0054, milestonePrice would be 0.006
function milestoneBuyPrice({ currentPrice }: { currentPrice: bigint }) {
  // if currentPrice is 0.0054, base10GreaterThanPrice = 0.01
  let base10GreaterThanPrice = parseEther('0.001');
  while (currentPrice >= base10GreaterThanPrice) {
    base10GreaterThanPrice = base10GreaterThanPrice * 10n;
  }
  // decimalIncrement = 0.01 / 10 = 0.001
  const decimalIncrement = base10GreaterThanPrice / 10n;
  // Milestone price = (floor(0.0054/0.001)+1) * 0.001 = 0.006
  return (currentPrice / decimalIncrement + 1n) * decimalIncrement;
}

// Computes the next decimal increment from the current price, and the number of mints to reach that price
export function milestonePriceAndQuantity(args: Omit<BuyPriceWithBufferArgs, 'quantity'>) {
  const currentPrice = totalBuyPriceWithBuffer({ ...args, quantity: 1 });
  const milestonePrice = milestoneBuyPrice({ currentPrice });

  // binary search the first quantity that makes totalBuyPriceWithBuffer > milestonePrice
  let low = 0;
  let high = 1_000_000;
  let mid = 0;
  let total;

  while (low < high) {
    mid = Math.floor((low + high) / 2);
    total = totalBuyPriceWithBuffer({
      ...args,
      currentSupply: args.currentSupply + mid,
      quantity: 1,
    });

    if (total < milestonePrice) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return {
    currentPriceInWei: currentPrice.toString(),
    milestonePriceInWei: milestonePrice.toString(),
    tokensToMilestone: low,
  };
}
